import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { css } from 'aphrodite';
import { observer } from 'mobx-react';
import React from 'react';
import { ContentCalendar } from '.';
import { CampaignType } from '../../../models/AdminModels';
import { useUserSession } from '../../../models/hooks/appStateHooks';
import { usePersistChangeInBrowserStorage } from '../../../models/hooks/usePersistChangeInBrowserStorage';
import { storagePrefixExcludedFromClear } from '../../../models/Storage';
import {
	AppDrawer,
	AppDrawerContent,
	AppDrawerToolbar,
	AppDrawerToolbarButton,
	AppDrawerToolbarButtonAiAssistant,
	StickyAppDrawerContent,
} from '../../components-v2/Drawer/AppDrawer';
import { TextSearchIcon } from '../../components/svgs/icons/TextSearchIcon';
import { bs } from '../../styles/styles';
import { DefaultUniversalAssistantChat } from '../templates/AssistantChat';
import { ContentCalendarTemplateBrowserWrapper } from './ContentCalendarTemplateBrowserWrapper';
import { styleSheet } from './styles';

function CustomerContentCalendarPageBase() {
	const [drawerValue, setDrawerValue] = React.useState<string>('templates');
	const contentCalendarRef = React.useRef<any>(null);
	const userSession = useUserSession();
	const [isSuggestionsOn, setIsSuggestionsOn] = React.useState(false);

	const isBlogsEnabled = userSession.account?.features?.blogFeature?.enabled;
	const isSocialMediaEnabled = userSession.account?.features?.socialMedia?.enabled;

	const campaignTypes = React.useMemo<CampaignType[]>(() => {
		const types = [CampaignType.Email];
		if (isSocialMediaEnabled) {
			types.push(CampaignType.Social);
		}
		if (isBlogsEnabled) {
			types.push(CampaignType.Blog);
		}
		return types;
	}, [isBlogsEnabled, isSocialMediaEnabled]);

	const [templateBrowserCampaignType, setTemplateBrowserCampaignType] = usePersistChangeInBrowserStorage<CampaignType>({
		storageKey: `${storagePrefixExcludedFromClear}-content-calendar-template-campaign-type`,
		storageType: 'localStorage',
		defaultIfEmpty: CampaignType.Email,
		// skipPersistFn: () => props.impersonationContext?.isValid,
		setInitialValue: storedValue => {
			if (!campaignTypes.includes(storedValue)) {
				return campaignTypes[0];
			}
			return storedValue;
		},
	});

	React.useEffect(() => {
		const cleanup = monitorForElements({
			onDragStart() {
				// Nothing to do here
			},
			onDrop({ source, location }) {
				const destination = location.current.dropTargets[0];
				if (!destination) {
					return;
				}

				const sourceData = source.data as { type: string; id: string; index: number };
				const destinationData = destination.data as { type: string; id: string };

				// Convert Pragmatic DnD event to react-beautiful-dnd format for compatibility
				const dropResult = {
					draggableId: sourceData.id,
					type: sourceData.type,
					source: {
						droppableId: 'campaignTemplatesList', // Template browser ID
						index: sourceData.index,
					},
					destination: {
						droppableId: destinationData.id,
						index: 0, // Calendar doesn't use index
					},
					reason: 'DROP' as const,
					mode: 'FLUID' as const,
					combine: null as any,
				};

				// Call the ContentCalendar's internal handler
				if (contentCalendarRef.current?.onDragEndInternal) {
					contentCalendarRef.current.onDragEndInternal(dropResult);
				}
			},
		});

		return cleanup;
	}, []);

	return (
		<div className={css(styleSheet.pageRoot)}>
			<ContentCalendar
				ref={contentCalendarRef}
				styles={[bs.px5]}
				droppableType='campaignTemplateCard'
				templateBrowserCampaignType={templateBrowserCampaignType}
				setTemplateBrowserCampaignType={setTemplateBrowserCampaignType}
				onSuggestionsToggleChanged={setIsSuggestionsOn}
			/>
			<AppDrawer value={drawerValue} onValueChange={setDrawerValue}>
				<AppDrawerContent value='templates'>
					<ContentCalendarTemplateBrowserWrapper
						templateBrowserCampaignType={templateBrowserCampaignType}
						setTemplateBrowserCampaignType={setTemplateBrowserCampaignType}
						isSuggestionsOn={isSuggestionsOn}
					/>
				</AppDrawerContent>
				<StickyAppDrawerContent value='ai-assistant'>
					<DefaultUniversalAssistantChat />
				</StickyAppDrawerContent>
				<AppDrawerToolbar>
					<AppDrawerToolbarButtonAiAssistant value='ai-assistant' />
					<AppDrawerToolbarButton value='templates' tooltip='Browse templates'>
						<TextSearchIcon />
					</AppDrawerToolbarButton>
				</AppDrawerToolbar>
			</AppDrawer>
		</div>
	);
}

export const CustomerContentCalendarPage = observer(CustomerContentCalendarPageBase);
